import React, { useState, useEffect, useRef } from 'react';
import {
  Video,
  Wand2,
  Upload,
  Scissors,
  Type,
  Gauge,
  Layers,
  Music,
  Sliders,
  Play,
  Pause,
  Download,
  Trash2,
  RefreshCw,
  Check,
  X,
  Loader2,
  ChevronRight,
  Film,
  Clock,
  Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Slider } from './ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { api, isUpgradeError } from '../context/AuthContext';
import { CreditCost } from './CreditMeter';
import useCredits, { creditCost } from '../hooks/useCredits';

// Video Player Component
const VideoPlayer = ({ src, onTimeUpdate, onLoadedMetadata }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Reset state when src changes
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [src]);

  // Cleanup: pause video when component unmounts to prevent "play() interrupted" errors
  useEffect(() => {
    return () => {
      const video = videoRef.current;
      if (video) {
        video.pause();
      }
    };
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        // Handle play() promise to prevent uncaught promise rejection
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => setIsPlaying(true))
            .catch((error) => {
              // Ignore abort errors (expected when video is removed)
              if (error.name !== 'AbortError') {
                console.error('Video play error:', error);
              }
              setIsPlaying(false);
            });
        }
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      onTimeUpdate?.(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      onLoadedMetadata?.(videoRef.current.duration);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = percent * duration;
    }
  };

  return (
    <div className="relative bg-black rounded-lg overflow-hidden">
      <video
        ref={videoRef}
        src={src}
        className="w-full aspect-video"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        onError={(e) => console.error('Video load error:', e.target.error?.message || 'Unknown error', 'src:', src)}
        preload="auto"
      />
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
        <div 
          className="w-full h-1 bg-white/30 rounded cursor-pointer mb-3"
          onClick={handleSeek}
        >
          <div 
            className="h-full bg-emerald-500 rounded"
            style={{ width: `${(currentTime / duration) * 100}%` }}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-white/20"
              onClick={togglePlay}
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </Button>
            <span className="text-white text-sm">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Video Editor Component
export const VideoEditor = ({ isOpen, onClose, onVideoSelect }) => {
  const [activeTab, setActiveTab] = useState('generate');
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  
  // Generation state
  const [prompt, setPrompt] = useState('');
  const [format, setFormat] = useState('1280x720');
  const [duration, setDuration] = useState(4);
  const { credits, prices } = useCredits();
  // Seconds actually generated (and billed): clips are 3-10s, long videos are stitched from 10s segments
  const billedSeconds = duration > 10
    ? (Math.ceil(duration / 10) - 1) * 10 + Math.max(3, duration % 10 || 10)
    : Math.max(3, Math.min(10, duration));
  const videoCost = creditCost(prices, 'video_generation', { seconds: billedSeconds });
  const [addMusic, setAddMusic] = useState(true);
  const [musicStyle, setMusicStyle] = useState('auto');
  const [musicVolume, setMusicVolume] = useState(0.3);
  
  // Edit state
  const [editMode, setEditMode] = useState(null);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(10);
  const [textOverlay, setTextOverlay] = useState({ text: '', position: 'center', fontSize: 48, color: 'white' });
  const [speed, setSpeed] = useState(1);
  const [filters, setFilters] = useState({ brightness: 1, contrast: 1, saturation: 1, blur: 0 });
  const [videoDuration, setVideoDuration] = useState(10);
  
  // Merge state
  const [selectedForMerge, setSelectedForMerge] = useState([]);

  // Music style options
  const musicStyles = [
    { id: 'auto', name: 'Auto-detect', desc: 'Match to video mood' },
    { id: 'upbeat', name: 'Upbeat', desc: 'Corporate, positive' },
    { id: 'calm', name: 'Calm', desc: 'Peaceful, ambient' },
    { id: 'cinematic', name: 'Cinematic', desc: 'Epic, dramatic' },
    { id: 'happy', name: 'Happy', desc: 'Playful, cheerful' },
    { id: 'dramatic', name: 'Dramatic', desc: 'Intense, powerful' },
    { id: 'relaxing', name: 'Relaxing', desc: 'Piano, soft' },
    { id: 'energetic', name: 'Energetic', desc: 'Electronic, fast' },
    { id: 'nature', name: 'Nature', desc: 'Natural sounds' },
  ];

  useEffect(() => {
    if (isOpen) {
      loadVideos();
    }
  }, [isOpen]);

  useEffect(() => {
    let interval;
    // Include all in-progress states for long video generation
    const inProgressStates = ['pending', 'processing', 'adding_music', 'generating_segments', 'stitching_segments'];
    const isInProgress = inProgressStates.includes(generationStatus?.status) || 
                         generationStatus?.status?.startsWith('generating_segment');
    
    if (isInProgress) {
      interval = setInterval(async () => {
        try {
          const response = await api.get(`/video/status/${generationStatus.video_id}`);
          setGenerationStatus(response.data);
          
          if (response.data.status === 'completed') {
            const segments = response.data.segments || 1;
            toast.success(`Video generated successfully!${segments > 1 ? ` (${segments} segments stitched)` : ''}${response.data.has_music ? ' 🎵 Music added!' : ''}`);
            loadVideos();
            setSelectedVideo(response.data);
          } else if (response.data.status === 'failed') {
            toast.error(`Generation failed: ${response.data.error}`);
          }
        } catch (error) {
          console.error('Status check error:', error);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [generationStatus?.video_id, generationStatus?.status]);

  const loadVideos = async () => {
    try {
      const response = await api.get('/video/list');
      setVideos(response.data);
    } catch (error) {
      console.error('Error loading videos:', error);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a video description');
      return;
    }

    if (videoCost != null && credits && !credits.unlimited && videoCost >= 100
        && !window.confirm(`This ${duration}-second video uses ${videoCost} of your ${credits.balance} AI credits `
          + '(refunded if generation fails). Continue?')) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/video/generate', {
        prompt: prompt.trim(),
        duration,
        size: format,
        model: 'sora-2',
        add_music: addMusic,
        music_style: musicStyle,
        music_volume: musicVolume
      });
      
      setGenerationStatus(response.data);
      toast.success(addMusic ? 'Video generation started with background music!' : 'Video generation started!');
    } catch (error) {
      if (!isUpgradeError(error)) toast.error(error.response?.data?.detail || 'Failed to start generation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name);

    setIsLoading(true);
    try {
      const response = await api.post('/video/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Video uploaded successfully!');
      loadVideos();
      setSelectedVideo(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTrim = async () => {
    if (!selectedVideo) return;

    setIsLoading(true);
    try {
      const response = await api.post('/video/trim', {
        video_id: selectedVideo.video_id,
        start_time: trimStart,
        end_time: trimEnd
      });
      
      toast.success('Video trimmed successfully!');
      loadVideos();
      setSelectedVideo(response.data);
      setEditMode(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Trim failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTextOverlay = async () => {
    if (!selectedVideo || !textOverlay.text.trim()) return;

    setIsLoading(true);
    try {
      const response = await api.post('/video/text-overlay', {
        video_id: selectedVideo.video_id,
        text: textOverlay.text,
        position: textOverlay.position,
        font_size: textOverlay.fontSize,
        font_color: textOverlay.color
      });
      
      toast.success('Text overlay added!');
      loadVideos();
      setSelectedVideo(response.data);
      setEditMode(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add text');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSpeedChange = async () => {
    if (!selectedVideo) return;

    setIsLoading(true);
    try {
      const response = await api.post('/video/speed', {
        video_id: selectedVideo.video_id,
        speed
      });
      
      toast.success('Speed changed successfully!');
      loadVideos();
      setSelectedVideo(response.data);
      setEditMode(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change speed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyFilters = async () => {
    if (!selectedVideo) return;

    setIsLoading(true);
    try {
      const response = await api.post('/video/filter', {
        video_id: selectedVideo.video_id,
        ...filters
      });
      
      toast.success('Filters applied!');
      loadVideos();
      setSelectedVideo(response.data);
      setEditMode(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply filters');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMerge = async () => {
    if (selectedForMerge.length < 2) {
      toast.error('Select at least 2 videos to merge');
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/video/merge', {
        video_ids: selectedForMerge
      });
      
      toast.success('Videos merged successfully!');
      loadVideos();
      setSelectedVideo(response.data);
      setSelectedForMerge([]);
      setEditMode(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Merge failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAudio = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedVideo) return;

    const formData = new FormData();
    formData.append('video_id', selectedVideo.video_id);
    formData.append('audio_file', file);
    formData.append('volume', '1.0');
    formData.append('replace_original', 'false');

    setIsLoading(true);
    try {
      const response = await api.post('/video/add-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Audio added successfully!');
      loadVideos();
      setSelectedVideo(response.data);
      setEditMode(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add audio');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (videoId) => {
    try {
      await api.delete(`/video/${videoId}`);
      toast.success('Video deleted');
      loadVideos();
      if (selectedVideo?.video_id === videoId) {
        setSelectedVideo(null);
      }
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const handleSelectForPost = () => {
    const videoUrl = getVideoUrl(selectedVideo);
    if (videoUrl) {
      onVideoSelect?.(videoUrl);
      onClose();
      toast.success('Video added to post!');
    } else {
      toast.error('No video selected');
    }
  };

  const getVideoUrl = (video) => {
    if (!video) return null;
    const relativeUrl = video.output_url || video.url || `/api/video/stream/${video.video_id}`;
    // Ensure we have an absolute URL for the video element
    if (relativeUrl.startsWith('http')) {
      return relativeUrl;
    }
    return `${process.env.REACT_APP_BACKEND_URL}${relativeUrl}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Film className="w-5 h-5 text-emerald-600" />
            Video Studio
            <Badge variant="secondary" className="bg-purple-100 text-purple-700">AI Powered</Badge>
          </DialogTitle>
          <DialogDescription>
            Generate AI videos, upload your own, or edit existing videos
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="generate" className="flex items-center gap-2">
              <Wand2 className="w-4 h-4" />
              Generate
            </TabsTrigger>
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Upload
            </TabsTrigger>
            <TabsTrigger value="edit" className="flex items-center gap-2">
              <Sliders className="w-4 h-4" />
              Edit
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto mt-4">
            {/* Generate Tab */}
            <TabsContent value="generate" className="space-y-4 m-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <Label>Video Description</Label>
                    <Textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Describe the video you want to create... e.g., 'A majestic eagle soaring over snow-capped mountains at sunset'"
                      className="h-32 mt-1"
                      data-testid="video-prompt-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Format</Label>
                      <Select value={format} onValueChange={setFormat}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1280x720">Landscape (16:9)</SelectItem>
                          <SelectItem value="1024x1024">Square (1:1)</SelectItem>
                          <SelectItem value="1024x1792">Portrait (9:16)</SelectItem>
                          <SelectItem value="1792x1024">Widescreen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Duration</Label>
                      <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="4">4 seconds</SelectItem>
                          <SelectItem value="8">8 seconds</SelectItem>
                          <SelectItem value="12">12 seconds</SelectItem>
                          <SelectItem value="24">24 seconds</SelectItem>
                          <SelectItem value="36">36 seconds</SelectItem>
                          <SelectItem value="48">48 seconds</SelectItem>
                          <SelectItem value="60">1 minute</SelectItem>
                          <SelectItem value="90">1.5 minutes</SelectItem>
                          <SelectItem value="120">2 minutes</SelectItem>
                        </SelectContent>
                      </Select>
                      {duration > 12 && (
                        <p className="text-xs text-purple-600 mt-1">
                          {Math.ceil(duration / 12)} segments will be auto-stitched
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Background Music Options */}
                  <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Music className="w-4 h-4 text-purple-600" />
                        <Label className="text-sm font-medium">Background Music</Label>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAddMusic(!addMusic)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          addMusic ? 'bg-purple-600' : 'bg-slate-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            addMusic ? 'translate-x-4' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    
                    {addMusic && (
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-slate-600">Music Style</Label>
                          <Select value={musicStyle} onValueChange={setMusicStyle}>
                            <SelectTrigger className="mt-1 h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {musicStyles.map((style) => (
                                <SelectItem key={style.id} value={style.id}>
                                  <span className="flex items-center gap-2">
                                    {style.name}
                                    <span className="text-xs text-slate-400">{style.desc}</span>
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Volume: {Math.round(musicVolume * 100)}%</Label>
                          <Slider
                            value={[musicVolume]}
                            min={0.1}
                            max={1}
                            step={0.1}
                            onValueChange={([v]) => setMusicVolume(v)}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleGenerate}
                    disabled={isLoading || !prompt.trim()}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    data-testid="generate-video-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Video {addMusic && '🎵'}
                        <CreditCost feature="video_generation" seconds={billedSeconds} />
                      </>
                    )}
                  </Button>

                  {generationStatus && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Generation Status</span>
                        <Badge variant={
                          generationStatus.status === 'completed' ? 'default' :
                          generationStatus.status === 'failed' ? 'destructive' : 'secondary'
                        }>
                          {generationStatus.status === 'adding_music' ? '🎵 Adding music...' : 
                           generationStatus.status === 'stitching_segments' ? '🎬 Stitching...' :
                           generationStatus.status?.startsWith('generating_segment') ? `🎬 Segment ${generationStatus.current_segment || '?'}/${generationStatus.total_segments || '?'}` :
                           generationStatus.status}
                        </Badge>
                      </div>
                      {(generationStatus.status === 'pending' || generationStatus.status === 'processing' || 
                        generationStatus.status === 'adding_music' || generationStatus.status === 'generating_segments' ||
                        generationStatus.status === 'stitching_segments' || generationStatus.status?.startsWith('generating_segment')) && (
                        <div className="space-y-2">
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div
                              className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${generationStatus.progress || 10}%` }}
                            />
                          </div>
                          <p className="text-xs text-slate-500">
                            {generationStatus.status === 'adding_music' 
                              ? 'Adding background music to your video...'
                              : generationStatus.status === 'stitching_segments'
                              ? 'Stitching video segments together...'
                              : generationStatus.status?.startsWith('generating_segment')
                              ? `Generating segment ${generationStatus.current_segment || '?'} of ${generationStatus.total_segments || '?'}...`
                              : generationStatus.is_long_video || generationStatus.segments > 1
                              ? `Generating ${generationStatus.segments || Math.ceil(duration / 12)} segments (may take ${Math.ceil(duration / 12) * 2}-${Math.ceil(duration / 12) * 5} minutes)`
                              : 'This may take 2-5 minutes depending on duration'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="bg-slate-100 rounded-lg p-4 flex items-center justify-center min-h-[300px]">
                  {generationStatus?.status === 'completed' ? (
                    <div className="w-full">
                      <VideoPlayer src={getVideoUrl(generationStatus)} />
                      <div className="mt-3 flex gap-2">
                        <Button size="sm" variant="outline" className="flex-1" asChild>
                          <a href={getVideoUrl(generationStatus)} download>
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </a>
                        </Button>
                        <Button size="sm" className="flex-1" onClick={() => {
                          setSelectedVideo(generationStatus);
                          setActiveTab('edit');
                        }}>
                          <Sliders className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-slate-500">
                      <Video className="w-16 h-16 mx-auto mb-3 opacity-30" />
                      <p>Generated video will appear here</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Upload Tab */}
            <TabsContent value="upload" className="space-y-4 m-0">
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleUpload}
                  className="hidden"
                  id="video-upload"
                  data-testid="video-upload-input"
                />
                <label
                  htmlFor="video-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-12 h-12 text-slate-400 mb-3" />
                  <span className="text-lg font-medium text-slate-700">
                    Click to upload a video
                  </span>
                  <span className="text-sm text-slate-500 mt-1">
                    MP4, MOV, AVI, WebM supported
                  </span>
                </label>
              </div>

              {/* Video Library */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Your Videos</h3>
                {videos.length === 0 ? (
                  <p className="text-slate-500 text-sm">No videos yet</p>
                ) : (
                  <div className="grid grid-cols-3 gap-3 max-h-[300px] overflow-y-auto">
                    {videos.map((video) => (
                      <div
                        key={video.video_id}
                        className={`relative group rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                          selectedVideo?.video_id === video.video_id
                            ? 'border-emerald-500 ring-2 ring-emerald-500/20'
                            : 'border-transparent hover:border-slate-300'
                        }`}
                        onClick={() => setSelectedVideo(video)}
                      >
                        <video
                          src={getVideoUrl(video)}
                          className="w-full aspect-video object-cover"
                          preload="metadata"
                          muted
                          playsInline
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <Button size="icon" variant="ghost" className="text-white h-8 w-8">
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-white h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(video.video_id);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                          <p className="text-white text-xs truncate">
                            {video.name || video.prompt?.slice(0, 30) || video.video_id}
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            <Badge variant="secondary" className="text-[10px] px-1 py-0">
                              {video.type}
                            </Badge>
                            {video.info?.duration && (
                              <span className="text-[10px] text-white/70">
                                {Math.round(video.info.duration)}s
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Edit Tab */}
            <TabsContent value="edit" className="space-y-4 m-0">
              {!selectedVideo ? (
                <div className="text-center py-12">
                  <Video className="w-16 h-16 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">Select a video from your library to edit</p>
                  <Button
                    variant="outline"
                    className="mt-3"
                    onClick={() => setActiveTab('upload')}
                  >
                    Go to Library
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {/* Video Preview */}
                  <div className="space-y-3">
                    <VideoPlayer
                      src={getVideoUrl(selectedVideo)}
                      onLoadedMetadata={(dur) => {
                        setVideoDuration(dur);
                        setTrimEnd(dur);
                      }}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1" asChild>
                        <a href={getVideoUrl(selectedVideo)} download>
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </a>
                      </Button>
                      <Button size="sm" className="flex-1" onClick={handleSelectForPost}>
                        <Check className="w-4 h-4 mr-1" />
                        Use in Post
                      </Button>
                    </div>
                  </div>

                  {/* Edit Tools */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm">Edit Tools</h3>
                    
                    {/* Tool Buttons */}
                    {!editMode && (
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          className="justify-start"
                          onClick={() => setEditMode('trim')}
                        >
                          <Scissors className="w-4 h-4 mr-2" />
                          Trim
                        </Button>
                        <Button
                          variant="outline"
                          className="justify-start"
                          onClick={() => setEditMode('text')}
                        >
                          <Type className="w-4 h-4 mr-2" />
                          Text Overlay
                        </Button>
                        <Button
                          variant="outline"
                          className="justify-start"
                          onClick={() => setEditMode('speed')}
                        >
                          <Gauge className="w-4 h-4 mr-2" />
                          Speed
                        </Button>
                        <Button
                          variant="outline"
                          className="justify-start"
                          onClick={() => setEditMode('filters')}
                        >
                          <Sliders className="w-4 h-4 mr-2" />
                          Filters
                        </Button>
                        <Button
                          variant="outline"
                          className="justify-start"
                          onClick={() => setEditMode('merge')}
                        >
                          <Layers className="w-4 h-4 mr-2" />
                          Merge
                        </Button>
                        <Button
                          variant="outline"
                          className="justify-start"
                          onClick={() => setEditMode('audio')}
                        >
                          <Music className="w-4 h-4 mr-2" />
                          Add Audio
                        </Button>
                      </div>
                    )}

                    {/* Trim Panel */}
                    {editMode === 'trim' && (
                      <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">Trim Video</h4>
                          <Button size="sm" variant="ghost" onClick={() => setEditMode(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs">Start Time: {trimStart.toFixed(1)}s</Label>
                            <Slider
                              value={[trimStart]}
                              min={0}
                              max={videoDuration}
                              step={0.1}
                              onValueChange={([v]) => setTrimStart(Math.min(v, trimEnd - 0.5))}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">End Time: {trimEnd.toFixed(1)}s</Label>
                            <Slider
                              value={[trimEnd]}
                              min={0}
                              max={videoDuration}
                              step={0.1}
                              onValueChange={([v]) => setTrimEnd(Math.max(v, trimStart + 0.5))}
                              className="mt-1"
                            />
                          </div>
                          <p className="text-xs text-slate-500">
                            Duration: {(trimEnd - trimStart).toFixed(1)}s
                          </p>
                        </div>
                        <Button onClick={handleTrim} disabled={isLoading} className="w-full">
                          {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Apply Trim
                        </Button>
                      </div>
                    )}

                    {/* Text Overlay Panel */}
                    {editMode === 'text' && (
                      <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">Add Text</h4>
                          <Button size="sm" variant="ghost" onClick={() => setEditMode(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs">Text</Label>
                            <Input
                              value={textOverlay.text}
                              onChange={(e) => setTextOverlay({ ...textOverlay, text: e.target.value })}
                              placeholder="Enter your text..."
                              className="mt-1"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Position</Label>
                              <Select
                                value={textOverlay.position}
                                onValueChange={(v) => setTextOverlay({ ...textOverlay, position: v })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="top">Top</SelectItem>
                                  <SelectItem value="center">Center</SelectItem>
                                  <SelectItem value="bottom">Bottom</SelectItem>
                                  <SelectItem value="top-left">Top Left</SelectItem>
                                  <SelectItem value="top-right">Top Right</SelectItem>
                                  <SelectItem value="bottom-left">Bottom Left</SelectItem>
                                  <SelectItem value="bottom-right">Bottom Right</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Font Size</Label>
                              <Select
                                value={String(textOverlay.fontSize)}
                                onValueChange={(v) => setTextOverlay({ ...textOverlay, fontSize: Number(v) })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="24">Small</SelectItem>
                                  <SelectItem value="36">Medium</SelectItem>
                                  <SelectItem value="48">Large</SelectItem>
                                  <SelectItem value="72">Extra Large</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Color</Label>
                            <Select
                              value={textOverlay.color}
                              onValueChange={(v) => setTextOverlay({ ...textOverlay, color: v })}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="white">White</SelectItem>
                                <SelectItem value="black">Black</SelectItem>
                                <SelectItem value="yellow">Yellow</SelectItem>
                                <SelectItem value="red">Red</SelectItem>
                                <SelectItem value="blue">Blue</SelectItem>
                                <SelectItem value="green">Green</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button onClick={handleTextOverlay} disabled={isLoading || !textOverlay.text} className="w-full">
                          {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Add Text
                        </Button>
                      </div>
                    )}

                    {/* Speed Panel */}
                    {editMode === 'speed' && (
                      <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">Change Speed</h4>
                          <Button size="sm" variant="ghost" onClick={() => setEditMode(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs">Speed: {speed}x</Label>
                            <Slider
                              value={[speed]}
                              min={0.25}
                              max={4}
                              step={0.25}
                              onValueChange={([v]) => setSpeed(v)}
                              className="mt-2"
                            />
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            {[0.5, 1, 1.5, 2].map((s) => (
                              <Button
                                key={s}
                                size="sm"
                                variant={speed === s ? 'default' : 'outline'}
                                onClick={() => setSpeed(s)}
                              >
                                {s}x
                              </Button>
                            ))}
                          </div>
                        </div>
                        <Button onClick={handleSpeedChange} disabled={isLoading} className="w-full">
                          {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Apply Speed
                        </Button>
                      </div>
                    )}

                    {/* Filters Panel */}
                    {editMode === 'filters' && (
                      <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">Filters</h4>
                          <Button size="sm" variant="ghost" onClick={() => setEditMode(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs">Brightness: {filters.brightness.toFixed(1)}</Label>
                            <Slider
                              value={[filters.brightness]}
                              min={0}
                              max={2}
                              step={0.1}
                              onValueChange={([v]) => setFilters({ ...filters, brightness: v })}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Contrast: {filters.contrast.toFixed(1)}</Label>
                            <Slider
                              value={[filters.contrast]}
                              min={0}
                              max={2}
                              step={0.1}
                              onValueChange={([v]) => setFilters({ ...filters, contrast: v })}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Saturation: {filters.saturation.toFixed(1)}</Label>
                            <Slider
                              value={[filters.saturation]}
                              min={0}
                              max={2}
                              step={0.1}
                              onValueChange={([v]) => setFilters({ ...filters, saturation: v })}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Blur: {filters.blur}</Label>
                            <Slider
                              value={[filters.blur]}
                              min={0}
                              max={10}
                              step={1}
                              onValueChange={([v]) => setFilters({ ...filters, blur: v })}
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <Button onClick={handleApplyFilters} disabled={isLoading} className="w-full">
                          {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Apply Filters
                        </Button>
                      </div>
                    )}

                    {/* Merge Panel */}
                    {editMode === 'merge' && (
                      <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">Merge Videos</h4>
                          <Button size="sm" variant="ghost" onClick={() => setEditMode(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500">
                          Select videos to merge (order matters)
                        </p>
                        <div className="grid grid-cols-3 gap-2 max-h-[200px] overflow-y-auto">
                          {videos.map((video) => (
                            <div
                              key={video.video_id}
                              className={`relative rounded cursor-pointer border-2 ${
                                selectedForMerge.includes(video.video_id)
                                  ? 'border-emerald-500'
                                  : 'border-transparent'
                              }`}
                              onClick={() => {
                                setSelectedForMerge((prev) =>
                                  prev.includes(video.video_id)
                                    ? prev.filter((id) => id !== video.video_id)
                                    : [...prev, video.video_id]
                                );
                              }}
                            >
                              <video
                                src={getVideoUrl(video)}
                                className="w-full aspect-video object-cover rounded"
                                preload="metadata"
                                muted
                                playsInline
                              />
                              {selectedForMerge.includes(video.video_id) && (
                                <div className="absolute top-1 right-1 bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                  {selectedForMerge.indexOf(video.video_id) + 1}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        <Button
                          onClick={handleMerge}
                          disabled={isLoading || selectedForMerge.length < 2}
                          className="w-full"
                        >
                          {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Merge {selectedForMerge.length} Videos
                        </Button>
                      </div>
                    )}

                    {/* Audio Panel */}
                    {editMode === 'audio' && (
                      <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">Add Audio</h4>
                          <Button size="sm" variant="ghost" onClick={() => setEditMode(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
                          <input
                            type="file"
                            accept="audio/*"
                            onChange={handleAddAudio}
                            className="hidden"
                            id="audio-upload"
                          />
                          <label htmlFor="audio-upload" className="cursor-pointer">
                            <Music className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                            <span className="text-sm text-slate-600">
                              Click to upload audio
                            </span>
                            <p className="text-xs text-slate-400 mt-1">MP3, WAV, AAC</p>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default VideoEditor;
